import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import ArtifactCard from '@/components/ArtifactCard';
import { loadPapers, loadConfig, getPaperById } from '@/lib/data';
import { humanize } from '@/lib/humanize';

function Row({ label, cells }) {
  return (
    <tr>
      <td className="compare-table__label">{label}</td>
      {cells.map((cell, i) => (
        <td key={i} className="compare-table__cell">{cell ?? '—'}</td>
      ))}
    </tr>
  );
}

export default function ComparePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [papers, setPapers] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    Promise.all([loadPapers(), loadConfig()]).then(([p, c]) => {
      setPapers(p);
      setConfig(c);
    });
  }, []);

  if (!papers || !config) {
    return (
      <div className="main-layout">
        <div className="loading">
          <div className="loading__spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const ids = (params.get('ids') || '').split(',').filter(Boolean);
  const selected = ids.map((id) => getPaperById(papers, id)).filter(Boolean);

  return (
    <div className="main-layout">
      <Header config={config} />
      <main className="main-content">
        <button className="btn btn--ghost paper-detail__back" onClick={() => navigate('/')}>← Back to Papers</button>
        <h1>Compare Papers</h1>

        {selected.length < 2 ? (
          <div className="empty-state">
            <h3>Select at least 2 papers</h3>
            <p>Go back to the Papers list, turn on compare mode, and pick two or more papers.</p>
          </div>
        ) : (
          <>
            <div className="compare-table-wrap">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th className="compare-table__label">Field</th>
                    {selected.map((p) => (
                      <th key={p._id} className="compare-table__cell compare-table__cell--header">{p.metadata.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <Row label="Authors" cells={selected.map((p) => p.metadata.authors?.join(', '))} />
                  <Row label="Year" cells={selected.map((p) => p.metadata.year)} />
                  <Row label="Track" cells={selected.map((p) => p.metadata.track)} />
                  <Row label={humanize('type_of_inquiry')} cells={selected.map((p) => (p.contribution.focus_intent?.type_of_inquiry || []).map(humanize).join(', '))} />
                  <Row label={humanize('type_of_theory')} cells={selected.map((p) => (p.contribution.research_components?.type_of_theory || []).map(humanize).join(', '))} />
                  <Row label={humanize('emphasis')} cells={selected.map((p) => (p.contribution.research_components?.emphasis || []).map(humanize).join(', '))} />
                  <Row label={humanize('hypothesis')} cells={selected.map((p) => (p.contribution.research_components?.hypothesis ? 'Yes' : 'No'))} />
                  <Row label="Number of Artifacts" cells={selected.map((p) => p.contribution.artifacts?.length || 0)} />
                  <Row label={humanize('keywords')} cells={selected.map((p) => (p.contribution.keywords || []).join(', '))} />
                </tbody>
              </table>
            </div>

            {selected.map((p) => (
              <section className="detail-section" key={p._id}>
                <h2 className="detail-section__title">{p.metadata.title} — {humanize('artifacts')}</h2>
                <div className="artifact-grid">
                  {(p.contribution.artifacts || []).map((artifact, i) => (
                    <ArtifactCard key={i} artifact={artifact} index={i} />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
